import { Box, Typography } from "@mui/material";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import type { UserAwaitingVerificationDto } from "Infrastructure/Api/Api";
import {
  AdditionalInfoStatus,
  RiskProfileStatus,
  BankVerificationStatus,
  IdentityVerificationStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { useUsersNotVerifiedQuery } from "Api/Queries/User/useUsersNotVerifiedQuery";
import { useUsersAwaitingIdentityVerificationQuery } from "Api/Queries/User/useUsersAwaitingIdentityVerificationQuery";
import { useIsCurrentUserCustomerOnboarding } from "Hooks/useIsCurrentUserCustomerOnboarding";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { appUrl } from "Utils/UrlUtils";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useMemo } from "react";
import { VerificationIcon } from "Components/Verification/ToBeVerified/VerificationIcon";
import type { UserDetailPageTab } from "Components/Users/UserDetailPageContent/UserDetailPageContent";
import { UserRolesCell } from "Components/Users/UserListTable/UserRolesCell";
import { getIdentityRoleFilterOptions } from "Utils/TableFilterUtils";
import { useServerTable } from "Hooks/useServerTable";
import { asString, asStringArray } from "Utils/FilterUtils";
import {
  AWAITING_VERIFICATION_SORT_COLUMN_MAP,
  type AwaitingVerificationParams,
} from "Api/Queries/User/awaitingVerificationTypes";

const RESOURCES = Resources.Pages.Content.Users.ToBeVerified;

const getSharedColumns = (
  t: TFunction,
): Array<MRT_ColumnDef<UserAwaitingVerificationDto>> => [
  { accessorKey: "login", header: t(Resources.Common.Login) },
  {
    accessorKey: "firstName",
    header: t(Resources.Common.FirstName),
    accessorFn: ({ firstName }) => firstName || "",
    size: 115,
  },
  {
    accessorKey: "lastName",
    header: t(Resources.Common.LastName),
    accessorFn: ({ lastName }) => lastName || "",
    size: 125,
  },
  {
    accessorKey: "companyName",
    header: t(Resources.Common.CompanyName),
    accessorFn: ({ companyName }) => companyName || "",
  },
  {
    accessorKey: "companyNumber",
    header: t(Resources.Common.CompanyNumber),
    accessorFn: ({ companyNumber }) => companyNumber || "",
    size: 89,
  },
  {
    accessorKey: "roleCode",
    header: t(Resources.Pages.Content.Users.TableHeaders.Role),
    Cell: ({ row }) => (
      <UserRolesCell
        roleCode={row.original.roleCode}
        identityRoles={row.original.identityRoles}
      />
    ),
    filterVariant: "multi-select",
    filterSelectOptions: getIdentityRoleFilterOptions(t),
    size: 100,
    // Multi-value cell — the legacy role column sort would not reflect what is displayed.
    enableSorting: false,
  },
];

const getVerificationColumns = (
  t: TFunction,
): Array<MRT_ColumnDef<UserAwaitingVerificationDto>> => [
  {
    accessorKey: "identityVerificationStatus",
    Header: () => (
      <Box display="flex" alignItems="center" justifyContent="center">
        <BadgeOutlinedIcon sx={{ width: 20, height: 20 }} />
      </Box>
    ),
    header: t(RESOURCES.IdentityVerification),
    Cell: ({ row }) => (
      <VerificationIcon
        isVerified={
          row.original.identityVerificationStatus ===
          IdentityVerificationStatus.Verified
        }
      />
    ),
    enableColumnFilter: false,
    size: 65,
  },
  {
    accessorKey: "bankVerificationStatus",
    Header: () => (
      <Box display="flex" alignItems="center" justifyContent="center">
        <AssuredWorkloadOutlinedIcon sx={{ width: 20, height: 20 }} />
      </Box>
    ),
    header: t(RESOURCES.BankVerification),
    Cell: ({ row }) => (
      <VerificationIcon
        isVerified={
          row.original.bankVerificationStatus ===
          BankVerificationStatus.Verified
        }
      />
    ),
    enableColumnFilter: false,
    size: 67,
  },
  {
    accessorKey: "riskProfile",
    header: t(RESOURCES.RiskProfile),
    Header: () => (
      <Box display="flex" alignItems="center" justifyContent="center">
        <WarningAmberOutlinedIcon sx={{ width: 20, height: 20 }} />
      </Box>
    ),
    Cell: ({ row }) => (
      <VerificationIcon
        isVerified={row.original.riskProfile === RiskProfileStatus.LowRisk}
      />
    ),
    enableColumnFilter: false,
    size: 65,
  },
  {
    accessorKey: "additionalInfoStatus",
    header: t(RESOURCES.AdditionalInfo),
    Header: () => (
      <Box display="flex" alignItems="center" justifyContent="center">
        <InfoOutlinedIcon sx={{ width: 20, height: 20 }} />
      </Box>
    ),
    Cell: ({ row }) => (
      <VerificationIcon
        isVerified={
          row.original.additionalInfoStatus === AdditionalInfoStatus.Verified
        }
      />
    ),
    enableColumnFilter: false,
    size: 65,
  },
];

export const UserVerificationTable = () => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();
  const isCustomerOnboarding = useIsCurrentUserCustomerOnboarding();

  const { queryParams, tableProps } =
    useServerTable<UserAwaitingVerificationDto>({
      defaultSorting: [{ id: "dateCreated", desc: true }],
      defaultPageSize: 5,
    });

  const apiParams = useMemo(
    (): AwaitingVerificationParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: queryParams.sortBy
        ? (AWAITING_VERIFICATION_SORT_COLUMN_MAP[queryParams.sortBy] ??
          queryParams.sortBy)
        : undefined,
      sortDirection: queryParams.sortDirection,
      login: asString(queryParams.filters.login),
      firstName: asString(queryParams.filters.firstName),
      lastName: asString(queryParams.filters.lastName),
      companyName: asString(queryParams.filters.companyName),
      companyNumber: asString(queryParams.filters.companyNumber),
      roleCodes: asStringArray(queryParams.filters.roleCode),
    }),
    [queryParams],
  );

  const allVerificationQuery = useUsersNotVerifiedQuery({
    enabled: !isCustomerOnboarding,
    params: apiParams,
  });
  const identityVerificationQuery = useUsersAwaitingIdentityVerificationQuery({
    enabled: isCustomerOnboarding,
    params: apiParams,
  });

  const { data, isLoading, isError } = isCustomerOnboarding
    ? identityVerificationQuery
    : allVerificationQuery;

  const columns = useMemo(
    () => [
      ...getSharedColumns(t),
      ...(isCustomerOnboarding ? [] : getVerificationColumns(t)),
    ],
    [t, isCustomerOnboarding],
  );

  const table = useMaterialReactTable<UserAwaitingVerificationDto>({
    ...tableProps,
    data: data?.items ?? [],
    columns,
    rowCount: data?.totalCount ?? 0,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      ...tableProps.state,
      isLoading,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => {
      const activeTab: UserDetailPageTab = isCustomerOnboarding
        ? "party"
        : "user";
      const url = `${appUrl("user-detail")}/${row.original.userID}?activeTab=${activeTab}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: { cursor: "pointer" },
      };
    },
  });

  if (isError) {
    return <ErrorText>{t(Resources.Pages.Content.Users.Error)}</ErrorText>;
  }

  return (
    <Box>
      <Typography variant="h4" component="h2" fontSize="1.125rem" mb={2}>
        {t(RESOURCES.Subtitles.UserVerification)}
      </Typography>
      <Box display="grid" alignItems="start">
        <MaterialReactTable table={table} />
      </Box>
    </Box>
  );
};
